import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  CheckCircle2,
  AlertCircle,
  X,
  Warehouse,
  MapPin,
  Clock3,
  NotebookPen,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { warehouseService } from "../../../services/warehouse.service";
import { locationService } from "../../../services/location.service";

function Label({ children, required = false }) {
  return (
    <div className="mb-1.5 text-[12px] font-semibold tracking-[0.02em] text-slate-600">
      {children} {required ? <span className="text-[#d86a78]">*</span> : null}
    </div>
  );
}

function Input({ className = "", hasError = false, ...props }) {
  return (
    <input
      {...props}
      className={
        "h-[50px] w-full rounded-[14px] border bg-white px-4 text-[13px] text-slate-700 placeholder:text-slate-400 outline-none transition-shadow " +
        (hasError
          ? "border-rose-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 "
          : "border-[#f2c6cc] focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] ") +
        className
      }
    />
  );
}

function Select({ className = "", hasError = false, children, ...props }) {
  return (
    <select
      {...props}
      className={
        "h-[50px] w-full rounded-[14px] border bg-white px-4 text-[13px] text-slate-700 placeholder:text-slate-400 outline-none transition-shadow " +
        (hasError
          ? "border-rose-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 "
          : "border-[#f2c6cc] focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d7db] ") +
        className
      }
    >
      {children}
    </select>
  );
}

function FieldError({ error }) {
  if (!error) return <div className="mt-1 min-h-[16px]" />;
  return <div className="mt-1 min-h-[16px] text-[11px] text-rose-600">{error}</div>;
}

function Popup({ open, type = "error", title, message, onClose }) {
  if (!open) return null;

  const isSuccess = type === "success";

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/35 px-4">
      <div className="w-full max-w-md rounded-[20px] bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex h-11 w-11 items-center justify-center rounded-full ${
                isSuccess ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-[#d86a78]"
              }`}
            >
              {isSuccess ? <CheckCircle2 size={22} /> : <AlertCircle size={22} />}
            </div>

            <div>
              <h3 className="text-[16px] font-semibold text-slate-800">{title}</h3>
              <p className="mt-1 text-[13px] leading-5 text-slate-500">{message}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className={`h-[40px] min-w-[110px] rounded-[10px] px-4 text-[12px] font-semibold text-white transition ${
              isSuccess ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#d86a78] hover:bg-[#c95b69]"
            }`}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionButton({ variant = "primary", children, disabled, ...props }) {
  const base =
    "h-[40px] w-[150px] rounded-[8px] text-[12px] font-semibold tracking-wide transition active:scale-[0.99]";
  const styles =
    variant === "primary"
      ? "bg-[#d86a78] text-white hover:bg-[#c95b69]"
      : "bg-[#6b6b6b] text-white hover:bg-[#5b5b5b]";

  return (
    <button
      type="button"
      disabled={disabled}
      className={`${base} ${styles} ${disabled ? "opacity-60 pointer-events-none" : ""}`}
      {...props}
    >
      {children}
    </button>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children, columns = "lg:grid-cols-3" }) {
  return (
    <section className="rounded-[24px] border border-[#f3c9cf] bg-white/95 p-5 shadow-[0_16px_40px_rgba(216,106,120,0.08)] md:p-6">
      <div className="mb-5 flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] bg-gradient-to-br from-[#fff1f4] to-[#ffe6ea] text-[#d86a78] shadow-sm">
          {Icon ? <Icon size={20} /> : null}
        </div>
        <div>
          <h2 className="text-[18px] font-bold text-slate-800">{title}</h2>
          {subtitle ? <p className="mt-1 text-[12px] leading-5 text-slate-500">{subtitle}</p> : null}
        </div>
      </div>
      <div className={`grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2 ${columns}`}>{children}</div>
    </section>
  );
}

function TipPill({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-[#f4d3d8] bg-white/85 px-3 py-1 text-[11px] font-semibold text-slate-500 shadow-sm">
      {children}
    </span>
  );
}

const requiredFields = {
  warehouseId: "Warehouse ID is required",
  warehouseName: "Warehouse Name is required",
  warehouseEmail: "Warehouse Email is required",
  warehousePhone: "Warehouse Phone is required",
  warehouseManager: "Warehouse Manager is required",
  managerEmpId: "Manager Employee ID is required",
  managerEmail: "Manager Email is required",
  address: "Address Line 1 is required",
  addressLine2: "Address Line 2 is required",
  pincode: "Pincode is required",
  country: "Country is required",
  state: "State is required",
  city: "City is required",
  longitude: "Longitude is required",
  latitude: "Latitude is required",
  dailyStartTime: "Daily Start Time is required",
  dailyOffTime: "Daily Off Time is required",
  orderRadiusKm: "Order Radius is required",
  maxOrdersSlot1: "Max Orders / Slot 1 is required",
  avgDeliveryTimeMins: "Average Delivery Time is required",
  bufferTimeMins: "Buffer Time is required",
  // maxOrdersSlot2: "Max Orders / Slot 2 is required",
  note: "Note is required",
};

const buildTimeOptions = () => {
  const options = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 1) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      options.push(`${hh}:${mm}`);
    }
  }
  return options;
};

const timeOptions = buildTimeOptions();

export default function AddNewWarehouse() {
  const navigate = useNavigate();
  const location = useLocation();

  const isEdit = location?.state?.mode === "edit";
  const editId = location?.state?.warehouseId || "";
  const prefill = location?.state?.warehouse || null;

  const [cityOptions, setCityOptions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const [form, setForm] = useState({
    warehouseId: "",
    warehouseName: "",
    warehouseEmail: "",
    warehousePhone: "",
    warehouseManager: "",
    managerEmpId: "",
    managerEmail: "",
    address: "",
    addressLine2: "",
    pincode: "",
    country: "IN",
    state: "",
    city: "",
    longitude: "",
    latitude: "",
    dailyStartTime: "",
    dailyOffTime: "",
    orderRadiusKm: "",
    maxOrdersSlot1: "",
    avgDeliveryTimeMins: "",
    bufferTimeMins: "",
    maxOrdersSlot2: "",
    note: "",
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [popup, setPopup] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });

  const [pinLoading, setPinLoading] = useState(false);
  const [pinErr, setPinErr] = useState("");
  const pinFetchTimerRef = useRef(null);

  const set = (k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
    setErrors((prev) => ({ ...prev, [k]: "" }));
  };

  const openPopup = ({ type = "error", title, message }) => {
    setPopup({ open: true, type, title, message });
  };

  const closePopup = () => {
    setPopup((s) => ({ ...s, open: false }));
  };

  const normalizeTime = (t) => {
    const v = String(t || "").trim();
    if (!v) return "";
    if (/^\d{2}:\d{2}:\d{2}$/.test(v)) return v.slice(0, 5);
    if (/^\d{2}:\d{2}$/.test(v)) return v;
    return v;
  };

  const filteredCityOptions = useMemo(() => {
    const q = String(form.city || "").trim().toLowerCase();
    if (!q) return cityOptions.slice(0, 12);

    return cityOptions
      .filter((city) => city.toLowerCase().includes(q))
      .slice(0, 12);
  }, [cityOptions, form.city]);

  useEffect(() => {
    const stateName = String(form.state || "").trim();
    const countryName =
      String(form.country || "").trim().toUpperCase() === "IN"
        ? "India"
        : String(form.country || "").trim();

    if (!stateName || !countryName) {
      setCityOptions([]);
      return;
    }

    let active = true;

    (async () => {
      try {
        setCityLoading(true);

        const rows = await locationService.searchCities({
          countryName,
          stateName,
        });

        if (!active) return;

        const names = rows
          .map((x) => x?.name || x?.city_name || x?.city || "")
          .filter(Boolean);

        setCityOptions([...new Set(names)]);
      } catch (e) {
        if (active) setCityOptions([]);
      } finally {
        if (active) setCityLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [form.state, form.country]);

  useEffect(() => {
    if (!isEdit) return;

    if (!editId) {
      navigate("/admin/dashboard/warehouse", { replace: true });
      return;
    }

    if (prefill) {
      const w = prefill;

      setForm((s) => ({
        ...s,
        warehouseId: w?.code || w?.warehouseId || s.warehouseId || "",
        warehouseName: w?.name || s.warehouseName || "",
        warehouseEmail: w?.email || s.warehouseEmail || "",
        warehousePhone: w?.phone || s.warehousePhone || "",
        warehouseManager:
          w?.managerName || w?.manager_name || w?.manager || s.warehouseManager || "",
        managerEmpId: s.managerEmpId || "",
        managerEmail: s.managerEmail || "",
        address:
          w?.address?.line1 ||
          w?.address?.addressLine1 ||
          w?.address_line1 ||
          s.address ||
          "",
        addressLine2:
          w?.address?.line2 ||
          w?.address?.addressLine2 ||
          w?.address_line2 ||
          s.addressLine2 ||
          "",
        pincode: w?.address?.pincode || w?.pincode || s.pincode || "",
        country: (w?.address?.country || w?.country || s.country || "IN").toUpperCase(),
        state: w?.address?.state || w?.state || s.state || "",
        city: w?.address?.city || w?.city || s.city || "",
      }));
    }

    (async () => {
      try {
        const res = await warehouseService.getWarehouse(editId);

        const w =
          res?.data?.data ||
          res?.data?.warehouse ||
          res?.warehouse ||
          res?.data ||
          res;

        setForm((s) => ({
          ...s,
          warehouseId: w?.code || w?.warehouseId || s.warehouseId || "",
          warehouseName: w?.name || s.warehouseName || "",
          warehouseEmail: w?.email || s.warehouseEmail || "",
          warehousePhone: w?.phone || s.warehousePhone || "",
          warehouseManager:
            w?.manager_name || w?.managerName || w?.manager || s.warehouseManager || "",
          managerEmpId: w?.manager_emp_id || w?.managerEmpId || s.managerEmpId || "",
          managerEmail: w?.manager_email || w?.managerEmail || s.managerEmail || "",
          address: w?.address_line1 || w?.address?.line1 || s.address || "",
          addressLine2: w?.address_line2 || w?.address?.line2 || s.addressLine2 || "",
          pincode: w?.pincode || w?.address?.pincode || s.pincode || "",
          country: (w?.country || w?.address?.country || s.country || "IN").toUpperCase(),
          state: w?.state || w?.address?.state || s.state || "",
          city: w?.city || w?.address?.city || s.city || "",
          longitude:
            w?.lng !== undefined && w?.lng !== null ? String(w.lng) : s.longitude || "",
          latitude:
            w?.lat !== undefined && w?.lat !== null ? String(w.lat) : s.latitude || "",
          dailyStartTime: normalizeTime(
            w?.opening_time || w?.openingTime || w?.daily_start_time || s.dailyStartTime || ""
          ),
          dailyOffTime: normalizeTime(
            w?.closing_time || w?.closingTime || w?.daily_off_time || s.dailyOffTime || ""
          ),
          orderRadiusKm:
            w?.order_radius_km !== undefined && w?.order_radius_km !== null
              ? String(w.order_radius_km)
              : s.orderRadiusKm || "",
          maxOrdersSlot1:
            w?.max_active_orders !== undefined && w?.max_active_orders !== null
              ? String(w.max_active_orders)
              : s.maxOrdersSlot1 || "",
          avgDeliveryTimeMins:
            w?.avg_delivery_time_mins !== undefined && w?.avg_delivery_time_mins !== null
              ? String(w.avg_delivery_time_mins)
              : s.avgDeliveryTimeMins || "",
          bufferTimeMins:
            w?.buffer_time_mins !== undefined && w?.buffer_time_mins !== null
              ? String(w.buffer_time_mins)
              : s.bufferTimeMins || "",
          maxOrdersSlot2:
            w?.max_orders_slot_2 !== undefined && w?.max_orders_slot_2 !== null
              ? String(w.max_orders_slot_2)
              : s.maxOrdersSlot2 || "",
          note: w?.notes || w?.note || s.note || "",
        }));
      } catch (e) {
        openPopup({
          type: "error",
          title: "Failed",
          message: e?.message || "Failed to load warehouse details",
        });
      }
    })();
  }, [isEdit, editId, prefill, navigate]);

  useEffect(() => {
    const pin = String(form.pincode || "").trim();

    if (pinFetchTimerRef.current) clearTimeout(pinFetchTimerRef.current);

    if (!/^\d{6}$/.test(pin)) {
      setPinErr("");
      setPinLoading(false);
      return;
    }

    pinFetchTimerRef.current = setTimeout(async () => {
      try {
        setPinErr("");
        setPinLoading(true);

        const normalizeCountry = (c) => {
          const v = String(c || "").trim().toUpperCase();
          if (!v) return "IN";
          if (v === "INDIA") return "IN";
          if (v.length > 2) return v.slice(0, 2);
          return v;
        };

        const country = normalizeCountry(form.country);
        const url = `https://pincode.indraq.info/api/v1/postal/${encodeURIComponent(
          pin
        )}?country=${encodeURIComponent(country)}`;

        const res = await fetch(url);
        const data = await res.json();

        if (!res.ok || !data?.ok) {
          throw new Error(data?.message || "Invalid pincode");
        }

        const first = Array.isArray(data?.data) ? data.data[0] : null;
        if (!first) throw new Error("No location found for this pincode");

        setForm((s) => ({
          ...s,
          country: String(first.countryCode || "IN").toUpperCase(),
          state: first.state || s.state,
          city: first.city || s.city,
        }));

        setShowCityDropdown(true);
      } catch (e) {
        setPinErr(e?.message || "Failed to fetch pincode details");
      } finally {
        setPinLoading(false);
      }
    }, 450);

    return () => {
      if (pinFetchTimerRef.current) clearTimeout(pinFetchTimerRef.current);
    };
  }, [form.pincode, form.country]);

  const validateForm = () => {
    const nextErrors = {};

    Object.keys(requiredFields).forEach((key) => {
      const value = String(form[key] ?? "").trim();
      if (!value) nextErrors[key] = requiredFields[key];
    });

    if (form.warehouseEmail && !/^\S+@\S+\.\S+$/.test(form.warehouseEmail.trim())) {
      nextErrors.warehouseEmail = "Enter a valid warehouse email";
    }

    if (form.managerEmail && !/^\S+@\S+\.\S+$/.test(form.managerEmail.trim())) {
      nextErrors.managerEmail = "Enter a valid manager email";
    }

    if (form.pincode && !/^\d{6}$/.test(form.pincode.trim())) {
      nextErrors.pincode = "Pincode must be 6 digits";
    }

    if (form.warehousePhone && !/^\d{10,15}$/.test(form.warehousePhone.trim())) {
      nextErrors.warehousePhone = "Enter a valid phone number";
    }

    ["longitude", "latitude", "orderRadiusKm", "maxOrdersSlot1", "avgDeliveryTimeMins", "bufferTimeMins", "maxOrdersSlot2"].forEach(
      (key) => {
        if (String(form[key]).trim() && Number.isNaN(Number(form[key]))) {
          nextErrors[key] = "Enter a valid number";
        }
      }
    );

    if (form.dailyStartTime && form.dailyOffTime && form.dailyStartTime === form.dailyOffTime) {
      nextErrors.dailyOffTime = "Start and off time cannot be same";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      openPopup({
        type: "error",
        title: "Required fields missing",
        message: "Please fill all required fields correctly before submitting.",
      });
      return false;
    }

    return true;
  };

  async function onSubmit() {
    try {
      if (!validateForm()) return;

      setSaving(true);

      const toApiTime = (t) => {
        const v = String(t || "").trim();
        if (!v) return undefined;
        if (/^\d{2}:\d{2}:\d{2}$/.test(v)) return v.slice(0, 5);
        return v;
      };

      const payload = {
        code: String(form.warehouseId || "").trim(),
        name: String(form.warehouseName || "").trim(),
        email: String(form.warehouseEmail || "").trim(),
        phone: String(form.warehousePhone || "").trim(),

        managerEmpId: String(form.managerEmpId || "").trim(),
        manager_emp_id: String(form.managerEmpId || "").trim(),

        managerName: String(form.warehouseManager || "").trim(),
        manager_name: String(form.warehouseManager || "").trim(),

        managerEmail: String(form.managerEmail || "").trim(),
        manager_email: String(form.managerEmail || "").trim(),

        address: {
          line1: String(form.address || "").trim(),
          line2: String(form.addressLine2 || "").trim(),
          city: String(form.city || "").trim(),
          state: String(form.state || "").trim(),
          pincode: String(form.pincode || "").trim(),
          country:
            String(form.country || "").trim().toUpperCase() === "IN"
              ? "IN"
              : String(form.country || "").trim().toUpperCase(),
        },

        geo: {
          lat: Number(form.latitude),
          lng: Number(form.longitude),
        },

        openingTime: toApiTime(form.dailyStartTime),
        closingTime: toApiTime(form.dailyOffTime),

        orderRadiusKm: Number(form.orderRadiusKm),
        maxActiveOrders: Number(form.maxOrdersSlot1),
        avgDeliveryTimeMins: Number(form.avgDeliveryTimeMins),
        bufferTimeMins: Number(form.bufferTimeMins),
        maxOrdersSlot2: Number(form.maxOrdersSlot2),

        notes: String(form.note || "").trim(),
      };

      if (isEdit && editId) {
        await warehouseService.updateWarehouse(editId, payload);
      } else {
        await warehouseService.createWarehouse(payload);
      }

      openPopup({
        type: "success",
        title: isEdit ? "Warehouse Updated" : "Warehouse Created",
        message: isEdit
          ? "Warehouse details have been updated successfully."
          : "New warehouse has been created successfully.",
      });

      setTimeout(() => {
        navigate("/admin/dashboard/warehouse");
      }, 900);
    } catch (e) {
      const serverMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        (isEdit ? "Failed to update warehouse" : "Failed to create warehouse");

      openPopup({
        type: "error",
        title: "Submission Failed",
        message: serverMsg,
      });
    } finally {
      setSaving(false);
    }
  }

  const title = isEdit ? "UPDATE WAREHOUSE" : "ADD NEW WAREHOUSE";
  const primaryBtnText = saving
    ? isEdit
      ? "UPDATING..."
      : "SAVING..."
    : isEdit
    ? "UPDATE"
    : "SUBMIT";

  return (
    <div className="mx-auto w-full max-w-[1280px]">
      <div className="overflow-hidden rounded-[28px] border border-[#f3c9cf] bg-[linear-gradient(135deg,#fff8f9_0%,#fff5f7_55%,#ffffff_100%)] shadow-[0_18px_50px_rgba(216,106,120,0.08)]">
        <div className="grid gap-6 p-6 lg:grid-cols-[1.3fr_0.7fr] lg:p-8">
          <div>
            <div className="inline-flex items-center rounded-full border border-[#f3c9cf] bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#d86a78]">
              Warehouse setup workspace
            </div>
            <h1 className="mt-4 text-[26px] font-black tracking-[-0.02em] text-slate-900">
              {isEdit ? "Update warehouse profile" : "Create a new warehouse"}
            </h1>
            <p className="mt-3 max-w-2xl text-[13px] leading-6 text-slate-600">
              Fill the core warehouse identity, service area, shift timings and delivery capacity.
              Clean values here keep warehouse operations, dispatch planning and serviceability logic smooth.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <TipPill>Accurate location improves serviceability</TipPill>
              <TipPill>Operations timings drive slot availability</TipPill>
              <TipPill>Capacity values impact order flow</TipPill>
            </div>
          </div>

          <div className="rounded-[24px] border border-[#f3c9cf] bg-white/90 p-5 shadow-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-[18px] border border-[#f7d7db] bg-[#fff7f8] p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Mode</div>
                <div className="mt-2 text-[16px] font-bold text-slate-800">{isEdit ? "Edit" : "New setup"}</div>
              </div>
              <div className="rounded-[18px] border border-[#f7d7db] bg-[#fff7f8] p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Coverage</div>
                <div className="mt-2 text-[16px] font-bold text-slate-800">Store + dispatch</div>
              </div>
              <div className="rounded-[18px] border border-[#f7d7db] bg-[#fff7f8] p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Required</div>
                <div className="mt-2 text-[16px] font-bold text-slate-800">All marked fields</div>
              </div>
              <div className="rounded-[18px] border border-[#f7d7db] bg-[#fff7f8] p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Tip</div>
                <div className="mt-2 text-[16px] font-bold text-slate-800">Use real manager data</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <SectionCard
          icon={Warehouse}
          title="Warehouse identity"
          subtitle="Basic warehouse identity and owner details used across operations, reporting and employee mapping."
        >
          <div>
            <Label required>Warehouse ID</Label>
            <Input
              value={form.warehouseId}
              onChange={(e) => set("warehouseId", e.target.value)}
              placeholder="e.g. WH-nefazo-001"
              hasError={!!errors.warehouseId}
            />
            <FieldError error={errors.warehouseId} />
          </div>

          <div>
            <Label required>Warehouse Name</Label>
            <Input
              value={form.warehouseName}
              onChange={(e) => set("warehouseName", e.target.value)}
              placeholder="e.g. Central Warehouse"
              hasError={!!errors.warehouseName}
            />
            <FieldError error={errors.warehouseName} />
          </div>

          <div>
            <Label required>Warehouse Email</Label>
            <Input
              value={form.warehouseEmail}
              onChange={(e) => set("warehouseEmail", e.target.value)}
              placeholder="warehouse@example.com"
              hasError={!!errors.warehouseEmail}
            />
            <FieldError error={errors.warehouseEmail} />
          </div>

          <div>
            <Label required>Warehouse Phone</Label>
            <Input
              value={form.warehousePhone}
              onChange={(e) => set("warehousePhone", e.target.value.replace(/\D/g, "").slice(0, 15))}
              placeholder="e.g. 9876543210"
              hasError={!!errors.warehousePhone}
            />
            <FieldError error={errors.warehousePhone} />
          </div>

          <div>
            <Label required>Warehouse Manager</Label>
            <Input
              value={form.warehouseManager}
              onChange={(e) => set("warehouseManager", e.target.value)}
              placeholder="e.g. Rohan Sharma"
              hasError={!!errors.warehouseManager}
            />
            <FieldError error={errors.warehouseManager} />
          </div>

          <div>
            <Label required>Manager Employee ID</Label>
            <Input
              value={form.managerEmpId}
              onChange={(e) => set("managerEmpId", e.target.value)}
              placeholder="e.g. EMP-WH-102"
              hasError={!!errors.managerEmpId}
            />
            <FieldError error={errors.managerEmpId} />
          </div>

          <div className="lg:col-span-3">
            <Label required>Manager Email</Label>
            <Input
              value={form.managerEmail}
              onChange={(e) => set("managerEmail", e.target.value)}
              placeholder="manager@example.com"
              hasError={!!errors.managerEmail}
            />
            <FieldError error={errors.managerEmail} />
          </div>
        </SectionCard>

        <SectionCard
          icon={MapPin}
          title="Address & service location"
          subtitle="This information powers pincode mapping, delivery radius, serviceability checks and dispatch visibility."
        >
          <div className="md:col-span-2 lg:col-span-2">
            <Label required>Address Line 1</Label>
            <Input
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              placeholder="Street, building name, area"
              hasError={!!errors.address}
            />
            <FieldError error={errors.address} />
          </div>

          <div>
            <Label required>Pincode</Label>
            <Input
              value={form.pincode}
              onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6 digit pincode"
              hasError={!!errors.pincode || !!pinErr}
            />
            <div className="mt-1 min-h-[16px] text-[11px]">
              {pinLoading ? <span className="text-slate-500">Fetching location…</span> : null}
              {!pinLoading && (errors.pincode || pinErr) ? (
                <span className="text-rose-600">{errors.pincode || pinErr}</span>
              ) : null}
            </div>
          </div>

          <div className="md:col-span-2 lg:col-span-2">
            <Label required>Address Line 2</Label>
            <Input
              value={form.addressLine2}
              onChange={(e) => set("addressLine2", e.target.value)}
              placeholder="Landmark, floor, nearby point"
              hasError={!!errors.addressLine2}
            />
            <FieldError error={errors.addressLine2} />
          </div>

          <div>
            <Label required>Country</Label>
            <Input
              value={form.country === "IN" ? "India" : form.country}
              onChange={(e) => {
                const value = e.target.value.trim();
                if (!value) {
                  set("country", "");
                  return;
                }
                if (value.toUpperCase() === "IN" || value.toLowerCase() === "india") {
                  set("country", "IN");
                } else {
                  set("country", value);
                }
              }}
              placeholder="India"
              hasError={!!errors.country}
            />
            <FieldError error={errors.country} />
          </div>

          <div>
            <Label required>State</Label>
            <Input
              value={form.state}
              onChange={(e) => set("state", e.target.value)}
              placeholder="e.g. Punjab"
              hasError={!!errors.state}
            />
            <FieldError error={errors.state} />
          </div>

          <div className="relative">
            <Label required>City</Label>
            <div className="relative">
              <Input
                value={form.city}
                onChange={(e) => {
                  set("city", e.target.value);
                  setShowCityDropdown(true);
                }}
                onFocus={() => setShowCityDropdown(true)}
                onBlur={() => {
                  setTimeout(() => setShowCityDropdown(false), 200);
                }}
                placeholder={cityLoading ? "Loading cities..." : "Enter or select city"}
                autoComplete="off"
                className="pr-10"
                hasError={!!errors.city}
              />

              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setShowCityDropdown((prev) => !prev);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
              >
                <ChevronDown size={16} />
              </button>
            </div>

            {showCityDropdown && filteredCityOptions.length > 0 ? (
              <div
                className="absolute left-0 right-0 top-[72px] z-50 max-h-56 overflow-auto rounded-[14px] border bg-white shadow-lg"
                style={{ borderColor: "#f2c6cc" }}
              >
                {cityLoading ? (
                  <div className="px-3 py-2 text-[12px] text-slate-400">Loading cities...</div>
                ) : (
                  filteredCityOptions.map((city) => (
                    <button
                      key={city}
                      type="button"
                      className="block w-full px-3 py-2 text-left text-[12px] text-slate-600 hover:bg-[#fff5f6]"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        set("city", city);
                        setShowCityDropdown(false);
                      }}
                    >
                      {city}
                    </button>
                  ))
                )}
              </div>
            ) : null}

            <FieldError error={errors.city} />
          </div>

          <div>
            <Label required>Longitude</Label>
            <Input
              value={form.longitude}
              onChange={(e) => set("longitude", e.target.value)}
              placeholder="e.g. 75.8573"
              hasError={!!errors.longitude}
            />
            <FieldError error={errors.longitude} />
          </div>

          <div>
            <Label required>Latitude</Label>
            <Input
              value={form.latitude}
              onChange={(e) => set("latitude", e.target.value)}
              placeholder="e.g. 30.9000"
              hasError={!!errors.latitude}
            />
            <FieldError error={errors.latitude} />
          </div>
        </SectionCard>

        <SectionCard
          icon={Clock3}
          title="Operations & capacity"
          subtitle="Define working hours and order handling capacity so slotting and dispatch limits stay realistic."
        >
          <div>
            <Label required>Daily Start Time</Label>
            <Select
              value={form.dailyStartTime}
              onChange={(e) => set("dailyStartTime", e.target.value)}
              hasError={!!errors.dailyStartTime}
            >
              <option value="">Select start time</option>
              {timeOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </Select>
            <FieldError error={errors.dailyStartTime} />
          </div>

          <div>
            <Label required>Daily Off Time</Label>
            <Select
              value={form.dailyOffTime}
              onChange={(e) => set("dailyOffTime", e.target.value)}
              hasError={!!errors.dailyOffTime}
            >
              <option value="">Select off time</option>
              {timeOptions.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </Select>
            <FieldError error={errors.dailyOffTime} />
          </div>

          <div>
            <Label required>Order Radius (in KM)</Label>
            <Input
              value={form.orderRadiusKm}
              onChange={(e) => set("orderRadiusKm", e.target.value)}
              placeholder="e.g. 8"
              hasError={!!errors.orderRadiusKm}
            />
            <FieldError error={errors.orderRadiusKm} />
          </div>

          <div>
            <Label required>Max Orders / Slot 1</Label>
            <Input
              value={form.maxOrdersSlot1}
              onChange={(e) => set("maxOrdersSlot1", e.target.value)}
              placeholder="e.g. 120"
              hasError={!!errors.maxOrdersSlot1}
            />
            <FieldError error={errors.maxOrdersSlot1} />
          </div>

          <div>
            <Label required>Average Delivery Time (mins)</Label>
            <Input
              value={form.avgDeliveryTimeMins}
              onChange={(e) => set("avgDeliveryTimeMins", e.target.value)}
              placeholder="e.g. 25"
              hasError={!!errors.avgDeliveryTimeMins}
            />
            <FieldError error={errors.avgDeliveryTimeMins} />
          </div>

          <div>
            <Label required>Buffer Time (mins)</Label>
            <Input
              value={form.bufferTimeMins}
              onChange={(e) => set("bufferTimeMins", e.target.value)}
              placeholder="e.g. 10"
              hasError={!!errors.bufferTimeMins}
            />
            <FieldError error={errors.bufferTimeMins} />
          </div>
        </SectionCard>

        <SectionCard
          icon={NotebookPen}
          title="Operational note"
          subtitle="Add a short note for special instructions, area constraints or team context."
          columns="lg:grid-cols-1"
        >
          <div>
            <Label required>Note</Label>
            <Input
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
              placeholder="Any dispatch, staffing or area note..."
              hasError={!!errors.note}
            />
            <FieldError error={errors.note} />
          </div>
        </SectionCard>
      </div>

      <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-[22px] border border-[#f3c9cf] bg-white/90 px-5 py-4 shadow-sm sm:flex-row">
        <div>
          <div className="text-[14px] font-bold text-slate-800">Review once before saving</div>
          <div className="mt-1 text-[12px] text-slate-500">Use accurate manager, location and capacity details to avoid downstream operational issues.</div>
        </div>
        <div className="flex items-center gap-4">
          <ActionButton
            variant="secondary"
            onClick={() => navigate("/admin/dashboard/warehouse")}
            disabled={saving}
          >
            CANCEL
          </ActionButton>

          <ActionButton variant="primary" onClick={onSubmit} disabled={saving}>
            {primaryBtnText}
          </ActionButton>
        </div>
      </div>

      <Popup
        open={popup.open}
        type={popup.type}
        title={popup.title}
        message={popup.message}
        onClose={closePopup}
      />
    </div>
  );
}